"use client";

import { InboxIcon, type LucideIcon } from "lucide-react";

import { sourceIcon } from "@/components/content/icons/source-icons";
import { cn } from "@/lib/utils";

type SourceGlyphDescriptor = {
  Icon: LucideIcon;
  className: string;
  imageSrc?: string;
  label: string;
  monochrome?: boolean;
};

/*
 * Marks resolve through the icon registry so every surface shows the same
 * asset for a source; anything unregistered falls back to the inbox glyph.
 */
export function sourceGlyph(source: string): SourceGlyphDescriptor {
  const icon = sourceIcon(source);
  if (icon) {
    return {
      Icon: InboxIcon,
      imageSrc: icon.src,
      label: icon.label,
      className: "",
      ...(icon.monochrome ? { monochrome: true } : {}),
    };
  }
  return {
    Icon: InboxIcon,
    label: source || "Inbox item",
    className: "text-muted-foreground",
  };
}

export function SourceGlyph({
  source,
  className,
  iconClassName,
}: {
  source: string;
  className?: string;
  iconClassName?: string;
}) {
  const {
    Icon,
    className: glyphClassName,
    imageSrc,
    label,
    monochrome,
  } = sourceGlyph(source);
  return (
    <span
      className={cn(
        "flex size-4 shrink-0 items-center justify-center",
        glyphClassName,
        className,
      )}
      role="img"
      aria-label={label}
      title={label}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt=""
          className={cn(
            "size-4 object-contain",
            monochrome && "dark:invert",
            iconClassName,
          )}
        />
      ) : (
        <Icon className={cn("size-3.5", iconClassName)} aria-hidden="true" />
      )}
    </span>
  );
}
